import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CircleStop, FileText, Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useContractsQuery, useUpsertContractMutation } from '@/client/api/business/spend';
import type { BizContract } from '@/client/types/business/spend';
import { BusinessPageShell } from '../components/BusinessPageShell';
import { Field, filterClass, inputClass, textareaClass, useSpendLookups } from '../../Accounting/components/spend-ui';
import { DeleteConfirmDialog, EmptyState, RowMenu, StatusBadge } from '../../HumanResource/components/hr-ui';
import { money } from '../../HumanResource/components/comp-format';
import { formatDay } from '../../HumanResource/components/leave-format';

const CADENCE_LABEL: Record<BizContract['cadence'], string> = { one_off: 'one-off', monthly: 'per month', quarterly: 'per quarter', yearly: 'per year' };

type Form = { vendorId: string; title: string; startDate: string; endDate: string; autoRenew: boolean; noticeDays: string; value: string; currency: string; cadence: BizContract['cadence']; notes: string };

export default function ContractsPage() {
  const [params, setParams] = useSearchParams();
  const vendorFilter = params.get('vendor') ?? '';
  const contractsQ = useContractsQuery();
  const lookups = useSpendLookups();
  const upsert = useUpsertContractMutation();
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const [status, setStatus] = useState<'active' | 'ended'>('active');
  const [editing, setEditing] = useState<BizContract | null | 'new'>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [ending, setEnding] = useState<BizContract | null>(null);

  const contracts = (contractsQ.data ?? []).filter((c) => !vendorFilter || c.vendorId === vendorFilter);
  const shown = contracts.filter((c) => c.status === status);
  const soon = contracts.filter((c) => c.renewingSoon);

  useEffect(() => {
    if (editing === null) return setForm(null);
    const c = editing === 'new' ? null : editing;
    setForm({
      vendorId: c?.vendorId ?? vendorFilter,
      title: c?.title ?? '',
      startDate: c?.startDate ?? '',
      endDate: c?.endDate ?? '',
      autoRenew: c?.autoRenew ?? true,
      noticeDays: c?.noticeDays == null ? '30' : String(c.noticeDays),
      value: c?.value == null ? '' : String(c.value),
      currency: c?.currency ?? 'USD',
      cadence: c?.cadence ?? 'yearly',
      notes: c?.notes ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    upsert.mutate(
      {
        id: editing && editing !== 'new' ? editing.id : undefined,
        vendorId: form.vendorId,
        title: form.title,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        autoRenew: form.autoRenew,
        noticeDays: form.noticeDays === '' ? null : Number(form.noticeDays),
        value: form.value === '' ? null : Number(form.value),
        currency: form.currency.trim().toUpperCase(),
        cadence: form.cadence,
        notes: form.notes,
      },
      {
        onSuccess: () => {
          toast.success(editing === 'new' ? 'Contract added' : 'Contract updated');
          setEditing(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save the contract'),
      },
    );
  };

  return (
    <BusinessPageShell
      mock={false}
      title="Contracts"
      description="What we have signed with vendors, and when each can be cancelled. A contract is flagged 45 days before its cancel-by date."
      crumbs={[{ label: 'Procurement' }, { label: 'Contracts' }]}
      icon={FileText}
      actions={
        <PermissionButton code={PERMISSIONS.accounting.manage} className="gap-2" onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" /> Add contract
        </PermissionButton>
      }
      stats={[
        { label: 'Active', value: String(contracts.filter((c) => c.status === 'active').length) },
        { label: 'Cancel-by date coming up', value: String(soon.length), hint: soon[0] ? `${soon[0].title}: ${formatDay(soon[0].cancelBy as string)}` : undefined },
        { label: 'Auto-renewing', value: String(contracts.filter((c) => c.status === 'active' && c.autoRenew).length) },
      ]}
    >
      <div className="mb-4 flex flex-wrap gap-3">
        <select value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'ended')} className={filterClass}>
          <option value="active">Active</option>
          <option value="ended">Ended</option>
        </select>
        <select value={vendorFilter} onChange={(e) => setParams(e.target.value ? { vendor: e.target.value } : {})} className={filterClass}>
          <option value="">All vendors</option>
          {lookups.vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </div>

      {contractsQ.isError ? (
        <EmptyState label={`Could not load contracts: ${(contractsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : contractsQ.isLoading ? (
        <EmptyState label="Loading contracts…" />
      ) : shown.length === 0 ? (
        <EmptyState label="No contracts here." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Contract</th>
                <th className="px-4 py-3 font-medium">Vendor</th>
                <th className="px-4 py-3 font-medium">Value</th>
                <th className="px-4 py-3 font-medium">Term</th>
                <th className="px-4 py-3 font-medium">Cancel by</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((c) => (
                <tr key={c.id} className={canManage ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => canManage && setEditing(c)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{c.title}</p>
                    {c.autoRenew && <p className="text-xs text-gray-400">auto-renews</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{lookups.vendorName(c.vendorId)}</td>
                  <td className="px-4 py-3 text-gray-900">{c.value != null && c.currency ? `${money(c.value, c.currency)} ${CADENCE_LABEL[c.cadence]}` : '—'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {c.startDate ? formatDay(c.startDate) : '—'} – {c.endDate ? formatDay(c.endDate) : 'open-ended'}
                  </td>
                  <td className="px-4 py-3">
                    {c.cancelBy ? (
                      <>
                        <span className={c.renewingSoon ? 'font-medium text-amber-700' : 'text-gray-600'}>{formatDay(c.cancelBy)}</span>
                        {c.renewingSoon && (
                          <StatusBadge
                            label={c.daysToCancelBy != null && c.daysToCancelBy < 0 ? 'passed' : `${c.daysToCancelBy} days`}
                            className="ml-2 bg-amber-50 text-amber-800"
                          />
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      items={[
                        { label: 'Edit', icon: Pencil, hidden: !canManage, onSelect: () => setEditing(c) },
                        { label: 'Mark ended', icon: CircleStop, danger: true, hidden: !canManage || c.status !== 'active', onSelect: () => setEnding(c) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add contract' : 'Edit contract'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Vendor">
                  <select required value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })} className={inputClass}>
                    <option value="">Select a vendor</option>
                    {lookups.vendors.filter((v) => v.status === 'active' || v.id === form.vendorId).map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Title">
                  <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} placeholder="Pro plan, annual" />
                </Field>
                <Field label="Starts">
                  <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Ends (empty = open-ended)">
                  <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Value">
                  <input type="number" min={0} step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputClass} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Currency">
                    <input maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputClass} />
                  </Field>
                  <Field label="Per">
                    <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value as BizContract['cadence'] })} className={inputClass}>
                      <option value="one_off">One-off</option>
                      <option value="monthly">Month</option>
                      <option value="quarterly">Quarter</option>
                      <option value="yearly">Year</option>
                    </select>
                  </Field>
                </div>
                <Field label="Notice to cancel (days)">
                  <input type="number" min={0} value={form.noticeDays} onChange={(e) => setForm({ ...form, noticeDays: e.target.value })} className={inputClass} />
                </Field>
                <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.autoRenew} onChange={(e) => setForm({ ...form, autoRenew: e.target.checked })} /> Renews automatically
                </label>
              </div>
              <Field label="Notes">
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={textareaClass} />
              </Field>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsert.isPending}>
                  {upsert.isPending ? 'Saving…' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!ending}
        onOpenChange={(o) => !o && setEnding(null)}
        title={`Mark ${ending?.title} ended?`}
        description="It stops showing renewal warnings. Pause or end any recurring charge tied to it separately."
        actionLabel="Mark ended"
        onConfirm={() => {
          if (ending) upsert.mutate({ id: ending.id, status: 'ended' }, { onSuccess: () => toast.success('Contract ended'), onError: (e) => toast.error((e as Error).message) });
          setEnding(null);
        }}
      />
    </BusinessPageShell>
  );
}
